<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />
    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'notification-groups' }"
        :title="isEdit ? 'Editar grupo de notificação' : 'Novo grupo de notificação'"
        subtitle="Configure usuários, grupos de sensores e tipos de alarme para este grupo." />

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <!-- Coluna esquerda: Configuração -->
        <div class="space-y-6">
          <form class="space-y-6  border border-stroke bg-white p-6 shadow-sm" @submit.prevent="save">
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="block sm:col-span-2">
                <span class="mb-1 block text-sm font-medium text-slate-700">Nome *</span>
                <input
                  v-model="form.name"
                  type="text"
                  required
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Ex: Equipe Refrigeração" />
                <p v-if="errors.name" class="mt-1 text-xs text-red-600">{{ errors.name }}</p>
              </label>
              <label class="block">
                <span class="mb-1 block text-sm font-medium text-slate-700">Status</span>
                <label class="flex cursor-pointer items-center gap-2">
                  <input v-model="form.active" type="checkbox" class="h-4 w-4 border-stroke text-primary" />
                  <span class="text-sm text-slate-700">Ativo</span>
                </label>
              </label>
            </div>

            <label class="block">
              <span class="mb-1 block text-sm font-medium text-slate-700">Descrição</span>
              <textarea
                v-model="form.description"
                rows="2"
                class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Descrição opcional" />
            </label>

            <!-- Usuários -->
            <div class="border border-stroke bg-slate-50/50 p-4">
              <div class="mb-3 flex items-center justify-between">
                <h4 class="text-sm font-semibold text-slate-800">Usuários que receberão os alertas</h4>
                <button
                  type="button"
                  class="text-xs font-medium text-primary hover:underline"
                  @click="toggleSelectAllUsers">
                  {{ allUsersSelected ? 'Desmarcar todos' : 'Selecionar todos' }}
                </button>
              </div>
              <div class="max-h-48 space-y-2 overflow-y-auto">
                <label
                  v-for="user in companyUsers"
                  :key="user.id"
                  class="flex cursor-pointer items-center gap-3 border border-transparent px-3 py-2 hover:bg-white">
                  <input
                    v-model="form.user_ids"
                    :value="user.id"
                    type="checkbox"
                    class="h-4 w-4 border-stroke text-primary" />
                  <span class="text-sm text-slate-700">{{ user.name }}</span>
                  <span class="text-xs text-slate-500">{{ user.email }}</span>
                </label>
                <p v-if="!companyUsers.length" class="text-sm text-slate-500">Nenhum usuário na empresa.</p>
              </div>
            </div>

            <!-- Grupos de sensores -->
            <div class="border border-stroke bg-slate-50/50 p-4">
              <div class="mb-3 flex items-center justify-between">
                <h4 class="text-sm font-semibold text-slate-800">Grupos de sensores</h4>
                <router-link
                  :to="{ name: 'create-group' }"
                  class="text-xs font-medium text-primary hover:underline">
                  Criar grupo de sensores
                </router-link>
              </div>
              <div class="max-h-40 space-y-2 overflow-y-auto">
                <label
                  v-for="grp in sensorGroups"
                  :key="grp.id"
                  class="flex cursor-pointer items-center gap-3 border border-transparent px-3 py-2 hover:bg-white">
                  <input
                    v-model="form.group_ids"
                    :value="grp.id"
                    type="checkbox"
                    class="h-4 w-4 border-stroke text-primary" />
                  <span class="text-sm text-slate-700">{{ grp.name }}</span>
                </label>
                <p v-if="!sensorGroups.length" class="text-sm text-slate-500">
                  <router-link :to="{ name: 'create-group' }" class="text-primary hover:underline">Criar grupo</router-link>
                </p>
              </div>
            </div>

            <!-- Tipos de alarme -->
            <div class="border border-stroke bg-slate-50/50 p-4">
              <h4 class="mb-3 text-sm font-semibold text-slate-800">Tipos de alarme</h4>
              <div class="grid max-h-48 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
                <label
                  v-for="(label, key) in alarmTypeOptions"
                  :key="key"
                  class="flex cursor-pointer items-center gap-3 border border-transparent px-3 py-2 hover:bg-white">
                  <input
                    v-model="form.alarm_types"
                    :value="key"
                    type="checkbox"
                    class="h-4 w-4 border-stroke text-primary" />
                  <span class="text-sm text-slate-700">{{ label }}</span>
                </label>
              </div>
            </div>

            <div v-if="saveError" class="bg-red-50 p-3 text-sm text-red-600">{{ saveError }}</div>

            <div class="flex justify-end gap-3 border-t border-stroke pt-4">
              <router-link
                :to="{ name: 'notification-groups' }"
                class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Cancelar
              </router-link>
              <button
                type="submit"
                :disabled="isSaving"
                class="bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60">
                {{ isSaving ? 'Salvando...' : 'Salvar' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Coluna direita: Visualização e resumo -->
        <div class="space-y-6">
          <div class="sticky top-6 space-y-6">
            <div class="border border-stroke bg-white p-6 shadow-sm">
              <h3 class="mb-4 text-lg font-semibold text-slate-900">Resumo do grupo</h3>
              <div class="space-y-3">
                <div class="flex items-center justify-between bg-slate-50 px-4 py-3">
                  <span class="text-sm text-slate-600">Nome</span>
                  <span class="font-medium text-slate-900">{{ form.name || '—' }}</span>
                </div>
                <div class="flex items-center justify-between bg-slate-50 px-4 py-3">
                  <span class="text-sm text-slate-600">Usuários selecionados</span>
                  <span class="font-medium text-slate-900">{{ form.user_ids.length }}</span>
                </div>
                <div class="flex items-center justify-between bg-slate-50 px-4 py-3">
                  <span class="text-sm text-slate-600">Grupos de sensores</span>
                  <span class="font-medium text-slate-900">{{ form.group_ids.length }}</span>
                </div>
                <div class="flex items-center justify-between bg-slate-50 px-4 py-3">
                  <span class="text-sm text-slate-600">Tipos de alarme</span>
                  <span class="font-medium text-slate-900">{{ form.alarm_types.length }}</span>
                </div>
              </div>
            </div>

            <div class="border border-stroke bg-white p-6 shadow-sm">
              <h3 class="mb-4 text-lg font-semibold text-slate-900">Sensores incluídos</h3>
              <p class="mb-3 text-xs text-slate-500">
                Sensores dos grupos selecionados que receberão notificações.
              </p>
              <div v-if="previewSensors.length" class="max-h-64 overflow-y-auto">
                <div class="mt-4 px-9">
                  <span class="font-semibold">Sensores:</span>
                  <ul class="list-disc">
                    <button
                      v-for="sensor in previewSensors"
                      :key="sensor.id"
                      :class="[
                        getSensorColor(sensor.sensor_type),
                        sensor.has_open_alarm && 'sensor-with-open-alarm',
                      ]"
                      class="m-4 mx-1 inline-block bg-black whitespace-nowrap px-3 py-2 text-sm font-medium text-white opacity-90 hover:opacity-100"
                      type="button"
                      @click="openSensorDetails(sensor)">
                      {{ sensor.name }}
                    </button>
                  </ul>
                </div>
              </div>
              <p v-else class="border border-dashed border-stroke p-4 text-center text-sm text-slate-500">
                Selecione grupos de sensores para ver os sensores incluídos.
              </p>
            </div>

            <div v-if="selectedUsersPreview.length" class="border border-stroke bg-white p-6 shadow-sm">
              <h3 class="mb-3 text-sm font-semibold text-slate-900">Usuários que receberão alertas</h3>
              <div class="max-h-40 space-y-1 overflow-y-auto">
                <p v-for="u in selectedUsersPreview" :key="u.id" class="text-sm text-slate-700">{{ u.name }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<script setup lang="ts">
import GroupResource from '@/api/resources/group'
import NotificationGroupResource from '@/api/resources/notificationGroup'
import UserResource from '@/api/resources/user'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import type { IGroup } from '@/interfaces/entities/group'
import type { INotificationGroup } from '@/interfaces/entities/notificationGroup'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { ICompanyUser } from '@/interfaces/entities/user'
import { ALARM_TYPE_LABELS, getSensorColor } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const companyUsers = ref<ICompanyUser[]>([])
const sensorGroups = ref<IGroup[]>([])
const existingGroup = ref<INotificationGroup | null>(null)

const form = ref({
  name: '',
  description: '',
  active: true,
  user_ids: [] as number[],
  group_ids: [] as number[],
  alarm_types: [] as string[],
})

const errors = ref<Record<string, string>>({})
const saveError = ref('')
const isSaving = ref(false)

const isEdit = computed(() => route.name === 'notification-groups-edit' && !!route.params.id)
const alarmTypeOptions = ALARM_TYPE_LABELS

const allUsersSelected = computed(() => {
  if (!companyUsers.value.length) return false
  return form.value.user_ids.length === companyUsers.value.length
})

const selectedUsersPreview = computed(() =>
  companyUsers.value.filter((u) => form.value.user_ids.includes(u.id)),
)

const previewSensors = computed(() => {
  const ids = form.value.group_ids
  const groups = sensorGroups.value.filter((g) => ids.includes(g.id))
  const seen = new Set<number>()
  const list: ISensor[] = []
  for (const g of groups) {
    for (const s of g.sensors || []) {
      if (!seen.has(s.id)) {
        seen.add(s.id)
        list.push(s)
      }
    }
  }
  return list
})

const sensorGroupName = (sensorId: number) => {
  const g = sensorGroups.value.find((grp) => grp.sensors?.some((s) => s.id === sensorId))
  return g?.name ?? ''
}

const openSensorDetails = (sensor: ISensor) => {
  router.push({ name: 'sensor-detail', params: { id: String(sensor.id) } })
}

const toggleSelectAllUsers = () => {
  if (allUsersSelected.value) {
    form.value.user_ids = []
  } else {
    form.value.user_ids = companyUsers.value.map((u) => u.id)
  }
}

const loadExistingGroup = async () => {
  const id = route.params.id
  if (!id || id === 'new') return
  try {
    const data = await NotificationGroupResource.show(Number(id))
    existingGroup.value = data
    form.value = {
      name: data.name,
      description: data.description ?? '',
      active: data.active,
      user_ids: data.users?.map((u) => u.id) ?? [],
      group_ids: data.groups?.map((g) => g.id) ?? [],
      alarm_types: data.alarm_type_configs?.map((a) => a.alarm_type) ?? [],
    }
  } catch {
    router.replace({ name: 'notification-groups' })
  }
}

const fetchCompanyUsers = async () => {
  try {
    const users = await UserResource.getCompanyUsers()
    companyUsers.value = Array.isArray(users) ? users : users?.data ?? []
  } catch {
    companyUsers.value = []
  }
}

const fetchSensorGroups = async () => {
  try {
    const list = await GroupResource.getGroups()
    sensorGroups.value = Array.isArray(list) ? list : list?.data ?? []
  } catch {
    sensorGroups.value = []
  }
}

const save = async () => {
  errors.value = {}
  saveError.value = ''
  if (!form.value.name.trim()) {
    errors.value.name = 'Nome é obrigatório.'
    return
  }

  isSaving.value = true
  try {
    if (isEdit.value && existingGroup.value) {
      await NotificationGroupResource.update(existingGroup.value.id, {
        name: form.value.name.trim(),
        description: form.value.description?.trim() || undefined,
        active: form.value.active,
      })
      const currentUserIds = existingGroup.value.users?.map((u) => u.id) ?? []
      const currentGroupIds = existingGroup.value.groups?.map((g) => g.id) ?? []
      const currentAlarmTypes = existingGroup.value.alarm_type_configs?.map((a) => a.alarm_type) ?? []

      const toAddUsers = form.value.user_ids.filter((id) => !currentUserIds.includes(id))
      const toRemoveUsers = currentUserIds.filter((id) => !form.value.user_ids.includes(id))
      const toAddGroups = form.value.group_ids.filter((id) => !currentGroupIds.includes(id))
      const toRemoveGroups = currentGroupIds.filter((id) => !form.value.group_ids.includes(id))
      const toAddAlarmTypes = form.value.alarm_types.filter((t) => !currentAlarmTypes.includes(t))
      const toRemoveAlarmTypes = currentAlarmTypes.filter((t) => !form.value.alarm_types.includes(t))

      if (toAddUsers.length) await NotificationGroupResource.addUsers(existingGroup.value.id, toAddUsers)
      if (toRemoveUsers.length) await NotificationGroupResource.removeUsers(existingGroup.value.id, toRemoveUsers)
      if (toAddGroups.length) await NotificationGroupResource.addGroups(existingGroup.value.id, toAddGroups)
      if (toRemoveGroups.length) await NotificationGroupResource.removeGroups(existingGroup.value.id, toRemoveGroups)
      if (toAddAlarmTypes.length) await NotificationGroupResource.addAlarmTypes(existingGroup.value.id, toAddAlarmTypes)
      if (toRemoveAlarmTypes.length) await NotificationGroupResource.removeAlarmTypes(existingGroup.value.id, toRemoveAlarmTypes)
    } else {
      await NotificationGroupResource.create({
        name: form.value.name.trim(),
        description: form.value.description?.trim() || undefined,
        active: form.value.active,
        user_ids: form.value.user_ids,
        group_ids: form.value.group_ids,
        alarm_types: form.value.alarm_types,
      })
    }
    router.push({ name: 'notification-groups' })
  } catch (e: any) {
    const msg = e?.response?.data?.errors
    saveError.value = Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar.'
  } finally {
    isSaving.value = false
  }
}

onMounted(async () => {
  isLoading.value = true
  await Promise.all([fetchCompanyUsers(), fetchSensorGroups()])
  if (isEdit.value) {
    await loadExistingGroup()
  } else {
    form.value = { name: '', description: '', active: true, user_ids: [], group_ids: [], alarm_types: [] }
  }
  isLoading.value = false
})
</script>
